// The player icon: a trainer in their outfit with their companion creature
// walking beside them (the front-facing walk loop). One component used
// everywhere a trainer is shown as a person: leaderboard rows and podium,
// the profile popup header, chat lines and the player hub.
//
// Art owns how this looks: everything visual is in this one function and the
// "Player icon" block at the end of public/themes/codex.css (.picon*).
import { SPECIES } from '../shared/species.ts';
import type { CompanionRef } from '../shared/protocol.ts';
import { creatureIcon, spriteUrl, companionUrl, companionBg, awakenedArt, trainerSheetUrl } from './assets.ts';
import { esc } from './fmt.ts';
import { artOf } from './skins.ts';
import { trainerSprite } from './trainerart.ts';

/** sm: chat lines (face crop, tiny companion); md: board rows; lg: podium and popup header; xl: the player hub. */
export type PlayerIconSize = 'sm' | 'md' | 'lg' | 'xl';

export interface PlayerIconOpts {
  /** An outfit id, or a worn code with the trainer's look ("keeper~s3h2c4", shared/looks.ts). */
  outfit?: string;
  /** A look code that replaces the one in `outfit` (the signed-in trainer's own). */
  look?: string | null;
  comp?: CompanionRef | null;
  size?: PlayerIconSize;
  /** Accessible label (the trainer's name); the icon is decorative without it. */
  name?: string;
}

/**
 * A trainer icon with their companion, as HTML. Every size draws 1:1 or at 2x so the pixels stay crisp:
 * sm and md show the trainer as they stand on the hunt field (the front idle frame of the outfit's sheet), lg and
 * xl the larger menu still; the companion is its own walk loop drawn at 40 px (sm at half size, md) or 48 px (lg,
 * xl at 2x), never a scaled-down portrait.
 */
export function playerIcon(o: PlayerIconOpts): string {
  const size = o.size ?? 'md';
  const sprite = trainerSprite(o.outfit, o.look);
  const big = size === 'lg' || size === 'xl';
  const trainer = big || size === 'sm' ? spriteUrl(`${sprite}-down-0`) : trainerSheetUrl(sprite);
  const sp = o.comp ? SPECIES[o.comp.species] : undefined;
  const art = o.comp && sp ? artOf(o.comp.species, o.comp.skin) : '';
  const walk = art ? companionUrl(art, !!o.comp!.aura, big) : undefined;
  const pic = !o.comp || !sp ? ''
    : walk ? `<span class="walker${awakenedArt(o.comp.species) ? ' awk' : ''}" style="${companionBg(art, !!o.comp.aura, big)}"></span>`
    : creatureIcon(artOf(o.comp.species, o.comp.skin), !!o.comp.aura, '');
  const pal = o.comp && sp
    ? `<span class="picon-pal${o.comp.aura ? ' aura' : ''}" title="${esc(`Companion: ${o.comp.aura ? 'Aura ' : ''}${sp.name}`)}">${pic}</span>`
    : '';
  const label = o.name ? ` role="img" aria-label="${esc(o.name)}${sp ? esc(` with ${sp.name}`) : ''}"` : ' aria-hidden="true"';
  return `<span class="picon picon-${size}${pal ? ' has-pal' : ''}"${label}><span class="picon-tr" style="background-image:url('${trainer}')"></span>${pal}</span>`;
}
